import { useState } from 'react'
import { Campo, Casilla, Entrada, Selector } from '@/components/ui/Campo'
import { SelectorFecha } from '@/components/ui/SelectorFecha'
import { useAvisar } from '@/components/ui/avisador-contexto'
import {
  proyectoEntrada,
  type ProyectoAdmin,
  type ProyectoEntrada,
} from '@/lib/schemas/admin'
import { useGuardarProyecto } from './api'
import { formatearPeriodo } from './formato'
import { useFormulario } from './formulario'
import { ModalMarco } from './ModalMarco'

interface ModalProyectoProps {
  abierto: boolean
  alCerrar: () => void
  clienteId: string
  clienteNombre: string
  /** Sin proyecto: alta. */
  proyecto: ProyectoAdmin | null
  /** Otros proyectos del cliente, para "Copiar pilares de…". */
  otros: ProyectoAdmin[]
  alGuardar?: (p: ProyectoAdmin) => void
}

/** C8 · Nuevo / editar proyecto. El período es informativo (§8.7). */
export function ModalProyecto(props: ModalProyectoProps) {
  if (!props.abierto) return null
  return <Formulario key={props.proyecto?.id ?? 'nuevo'} {...props} />
}

function Formulario({
  abierto,
  alCerrar,
  clienteId,
  clienteNombre,
  proyecto,
  otros,
  alGuardar,
}: ModalProyectoProps) {
  const guardar = useGuardarProyecto()
  const avisar = useAvisar()
  const candidatos = otros.filter((o) => o.id !== proyecto?.id)
  const [valores, setValores] = useState<ProyectoEntrada>(() =>
    proyecto ? desde(proyecto) : vacio(clienteId, candidatos[0]?.id ?? null),
  )
  const [copiar, setCopiar] = useState(false)
  const form = useFormulario(
    proyectoEntrada,
    async (_datos, crudos) => {
      const p = await guardar.mutateAsync({
        id: proyecto?.id ?? null,
        datos: { ...crudos, copiarPilaresDe: copiar ? crudos.copiarPilaresDe : null },
      })
      avisar(proyecto ? 'Proyecto guardado' : `Proyecto ${p.nombre} creado`)
      alGuardar?.(p)
    },
    alCerrar,
  )

  const cambiar = <K extends keyof ProyectoEntrada>(k: K, v: ProyectoEntrada[K]) => {
    setValores((a) => ({ ...a, [k]: v }))
  }

  return (
    <ModalMarco
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={proyecto ? 'Editar proyecto' : 'Nuevo proyecto'}
      guardar="Guardar"
      ocupado={form.ocupado}
      error={form.errorGeneral}
      alCambiar={form.limpiar}
      alEnviar={() => {
        form.validarYEnviar(valores)
      }}
    >
      <Campo etiqueta="Cliente">
        {(p) => <Entrada id={p.id} value={clienteNombre} disabled readOnly />}
      </Campo>

      <Campo
        etiqueta="Nombre"
        ayuda="Sugerido: trimestre y año."
        error={form.errores.nombre}
      >
        {(p) => (
          <Entrada
            id={p.id}
            aria-invalid={p.invalido}
            aria-describedby={p.describedBy}
            value={valores.nombre}
            onChange={(e) => {
              cambiar('nombre', e.target.value)
            }}
            autoFocus
            maxLength={120}
            placeholder="Trimestre oct–dic 2026"
          />
        )}
      </Campo>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium">Período</legend>
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Desde" error={form.errores.periodoDesde}>
            {(p) => (
              <SelectorFecha
                id={p.id}
                opcional
                invalido={p.invalido}
                valor={valores.periodoDesde ?? ''}
                alCambiar={(fecha) => {
                  cambiar('periodoDesde', fecha)
                }}
              />
            )}
          </Campo>
          <Campo etiqueta="Hasta" error={form.errores.periodoHasta}>
            {(p) => (
              <SelectorFecha
                id={p.id}
                opcional
                invalido={p.invalido}
                valor={valores.periodoHasta ?? ''}
                alCambiar={(fecha) => {
                  cambiar('periodoHasta', fecha)
                }}
              />
            )}
          </Campo>
        </div>
        <p className="text-xs text-tinta-suave">
          Informativo. No limita las fechas de las publicaciones.
        </p>
      </fieldset>

      {!proyecto && candidatos.length > 0 && (
        <div className="flex flex-col gap-2">
          <Casilla
            etiqueta="Copiar pilares de otro proyecto de este cliente"
            checked={copiar}
            onCheckedChange={(v) => {
              setCopiar(v === true)
            }}
          />
          {copiar && (
            <Campo etiqueta="Proyecto de origen">
              {(p) => (
                <Selector
                  id={p.id}
                  value={valores.copiarPilaresDe ?? ''}
                  onChange={(e) => {
                    cambiar('copiarPilaresDe', e.target.value || null)
                  }}
                >
                  {candidatos.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.nombre} · {formatearPeriodo(o.periodoDesde, o.periodoHasta)}
                    </option>
                  ))}
                </Selector>
              )}
            </Campo>
          )}
        </div>
      )}
    </ModalMarco>
  )
}

const vacio = (
  clienteId: string,
  copiarPilaresDe: string | null = null,
): ProyectoEntrada => ({
  clienteId,
  nombre: '',
  periodoDesde: null,
  periodoHasta: null,
  copiarPilaresDe,
})

const desde = (p: ProyectoAdmin): ProyectoEntrada => ({
  clienteId: p.clienteId,
  nombre: p.nombre,
  periodoDesde: p.periodoDesde,
  periodoHasta: p.periodoHasta,
  copiarPilaresDe: null,
})
